import ChevronDown16 from '@carbon/icons-react/es/chevron--down/16';
import Search16 from '@carbon/icons-react/es/search/16';
import { useBoolean } from 'ahooks';
import React, { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import { components as SelectComponents } from 'react-select';
import { ProjectSource } from 'api/project';
import { ReactComponent as TaskTemplateIcon } from 'assets/svgs/save-task-template.svg';
import { Popover } from 'components/atoms/popover';
import { Select } from 'components/atoms/select';
import { Option, SingleValue } from 'components/atoms/select/types';
import {
  Divider,
  TriggerButton,
  UsedTemplateCaption,
  SelectContainer,
  AllTemplatesText,
} from './styles';

const DropdownIndicator = (props) => {
  return (
    <SelectComponents.DropdownIndicator {...props}>
      <Search16 />
    </SelectComponents.DropdownIndicator>
  );
};

const TemplateOption = (props, sourceTemplates: ProjectSource[]) => {
  const { label, data, ...rest } = props;

  if (data.value === null) {
    return (
      <>
        <AllTemplatesText>
          <SelectComponents.Option {...rest}>{label}</SelectComponents.Option>
        </AllTemplatesText>
        <Divider />
      </>
    );
  }

  const isTemplateUsed = sourceTemplates?.find(
    (source) => source.templateName === label,
  );

  return (
    <SelectComponents.Option {...rest}>
      {label}
      {isTemplateUsed && (
        <UsedTemplateCaption>
          <FormattedMessage id="projects.task_template.drawer.used_in_this_project" />
        </UsedTemplateCaption>
      )}
    </SelectComponents.Option>
  );
};

const selectStyles = {
  control: (provided) => ({
    ...provided,
    margin: 8,
  }),
  menu: () => ({ boxShadow: 'none' }),
};

interface Props {
  options: Option[];
  onChange: (value: SingleValue<number>) => void;
  selected?: Option;
  sourceTemplates?: ProjectSource[];
}

export const TemplateSelect: FC<Props> = ({
  options,
  selected,
  onChange,
  sourceTemplates = [],
}) => {
  const [visible, { toggle: toggleVisibility, setFalse: closePopover }] =
    useBoolean(false);

  const handleChange = (value: SingleValue) => {
    onChange(value);
    closePopover();
  };

  return (
    <Popover
      placement="bottomLeft"
      trigger={['click']}
      visible={visible}
      onVisibleChange={toggleVisibility}
      destroyTooltipOnHide
      content={
        <SelectContainer>
          <Select
            menuIsOpen
            autoFocus
            hideSelectedOptions={false}
            controlShouldRenderValue={false}
            components={{
              DropdownIndicator,
              IndicatorSeparator: () => null,
              Option: (props) => TemplateOption(props, sourceTemplates),
            }}
            styles={selectStyles}
            placeholder={<FormattedMessage id="placeholder.search" />}
            options={options}
            value={selected}
            onChange={handleChange}
          />
        </SelectContainer>
      }
    >
      {selected ? (
        <div>
          <TaskTemplateIcon />
          <TriggerButton
            type="link"
            icon={<ChevronDown16 />}
            iconPlacement="right"
          >
            {selected.label}
          </TriggerButton>
        </div>
      ) : null}
    </Popover>
  );
};
